/**
 * dsar-tab.tsx — Cài đặt → Pháp lý & Dữ liệu cá nhân → "Yêu cầu dữ liệu cá nhân".
 *
 * Luật Bảo vệ dữ liệu cá nhân 91/2025/QH15 + NĐ 356/2025/NĐ-CP: tiếp nhận + theo dõi hạn xử lý yêu cầu của
 * khách (chủ thể dữ liệu) muốn xem/xuất, sửa, xoá, hạn chế xử lý dữ liệu của chính họ.
 *
 * Phần "Xoá vĩnh viễn" là thao tác KHÔNG THỂ HOÀN TÁC — bắt buộc xác nhận 2 lần: (1) tick đã đọc cảnh báo,
 * (2) gõ lại đúng tên/SĐT hiển thị của khách. Nút chỉ hiện với quyền `contacts.delete_permanently`.
 */
import { useState } from 'react'
import { useEffect } from 'react'
import { toast } from 'sonner'
import dayjs from 'dayjs'
import { Trans, useTranslation } from 'react-i18next'
import { AlertTriangle, Download, Loader2, Plus, ShieldAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge, type BadgeProps } from '@/components/ui/badge'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Loading, ErrorState } from '@/components/shared/feedback'
import { apiError } from '@/lib/api-client'
import { i18n } from '@/lib/i18n'
import { useMyPermissions } from '@/hooks/use-settings'
import { useContacts } from '@/hooks/use-contacts'
import {
  DSAR_STATUS_LABEL, DSAR_TYPE_LABEL, type DsarRequest, type DsarStatus, type DsarType,
  downloadDsarExport, useCreateDsarRequest, useDsarRequest, useDsarRequests, useEraseDsarRequest, useUpdateDsarRequest,
} from '@/hooks/use-dsar'

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', get label() { return i18n.t('settings:dsar.statusAll') } },
  { value: 'pending', get label() { return DSAR_STATUS_LABEL.pending } },
  { value: 'in_progress', get label() { return DSAR_STATUS_LABEL.in_progress } },
  { value: 'done', get label() { return DSAR_STATUS_LABEL.done } },
  { value: 'rejected', get label() { return DSAR_STATUS_LABEL.rejected } },
]

function statusBadgeVariant(status: DsarStatus): BadgeProps['variant'] {
  switch (status) {
    case 'done': return 'success'
    case 'rejected': return 'secondary'
    case 'in_progress': return 'default'
    default: return 'warning'
  }
}

/** Hạn còn lại tính từ dueAt — màu cảnh báo khi gần/quá hạn (không phụ thuộc dsar-date.ts backend, chỉ hiển thị). */
function dueBadge(dueAt: string, status: DsarStatus) {
  const date = dayjs(dueAt).format('DD/MM/YYYY')
  if (status === 'done' || status === 'rejected') {
    return <span className="text-muted-foreground">{date}</span>
  }
  const ms = new Date(dueAt).getTime() - Date.now()
  const days = Math.ceil(ms / (24 * 60 * 60 * 1000))
  if (ms < 0) return <Badge variant="destructive">{i18n.t('settings:dsar.overdueBy', { days: Math.abs(days), date })}</Badge>
  if (days <= 2) return <Badge variant="warning">{i18n.t('settings:dsar.dueSoon', { days, date })}</Badge>
  return <span>{date} <span className="text-muted-foreground">{i18n.t('settings:dsar.dueRemaining', { days })}</span></span>
}

export function DsarTab() {
  const { t } = useTranslation('settings')
  const { data: perms } = useMyPermissions()
  const canCreate = perms?.has('dsar.create') ?? false
  const canUpdate = perms?.has('dsar.update') ?? false
  const canExport = perms?.has('dsar.export') ?? false
  const canErase = perms?.has('contacts.delete_permanently') ?? false

  const [status, setStatus] = useState('')
  const [overdue, setOverdue] = useState(false)
  const [page, setPage] = useState(1)
  const [openCreate, setOpenCreate] = useState(false)
  const [eraseTarget, setEraseTarget] = useState<DsarRequest | null>(null)

  const { data, isLoading, isError } = useDsarRequests({ status: status || undefined, overdue, page })
  const update = useUpdateDsarRequest()

  if (isError) return <ErrorState message={t('settings:dsar.loadError')} />

  const items = data?.items ?? []

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between gap-3 rounded-lg border bg-muted/40 p-3 text-xs text-muted-foreground">
        <p>{t('settings:dsar.intro')}</p>
        {canCreate && (
          <Button size="sm" onClick={() => setOpenCreate(true)} className="shrink-0">
            <Plus className="h-4 w-4" /> {t('settings:dsar.createAction')}
          </Button>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Select value={status || 'all'} onValueChange={(v) => { setStatus(v === 'all' ? '' : v); setPage(1) }}>
          <SelectTrigger className="h-8 w-44"><SelectValue /></SelectTrigger>
          <SelectContent>
            {STATUS_FILTERS.map((s) => (
              <SelectItem key={s.value || 'all'} value={s.value || 'all'}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <button
          type="button"
          onClick={() => { setOverdue((v) => !v); setPage(1) }}
          className={
            overdue
              ? 'rounded-md border border-destructive bg-destructive/10 px-2.5 py-1 text-xs font-semibold text-destructive'
              : 'rounded-md border px-2.5 py-1 text-xs font-semibold hover:bg-accent'
          }
        >
          {t('settings:dsar.overdueOnly')}
        </button>
      </div>

      {isLoading ? (
        <Loading />
      ) : items.length === 0 ? (
        <p className="rounded-lg border border-dashed py-10 text-center text-sm text-muted-foreground">
          {t('settings:dsar.emptyFiltered')}
        </p>
      ) : (
        <div className="overflow-x-auto rounded-lg border">
          <table className="w-full text-xs">
            <thead className="bg-muted/70 text-left">
              <tr>
                <th className="px-3 py-2">{t('settings:dsar.columns.contact')}</th>
                <th className="px-3 py-2">{t('settings:dsar.columns.type')}</th>
                <th className="px-3 py-2">{t('settings:dsar.columns.receivedAt')}</th>
                <th className="px-3 py-2">{t('settings:dsar.columns.dueAt')}</th>
                <th className="px-3 py-2">{t('settings:dsar.columns.status')}</th>
                <th className="px-3 py-2">{t('settings:dsar.columns.handledBy')}</th>
                <th className="px-3 py-2">{t('settings:dsar.columns.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-t align-middle hover:bg-accent/40">
                  <td className="px-3 py-1.5">
                    <div className="font-medium">
                      {r.contact.crmName || r.contact.fullName || r.contact.phone || t('settings:dsar.noName')}
                      {r.contact.deleted && <Badge variant="destructive" className="ml-1.5">{t('settings:dsar.erasedBadge')}</Badge>}
                    </div>
                    <div className="text-muted-foreground">{r.contact.phone}</div>
                  </td>
                  <td className="whitespace-nowrap px-3 py-1.5">{DSAR_TYPE_LABEL[r.type]}</td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{dayjs(r.receivedAt).format('DD/MM/YYYY')}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">{dueBadge(r.dueAt, r.status)}</td>
                  <td className="px-3 py-1.5"><Badge variant={statusBadgeVariant(r.status)}>{DSAR_STATUS_LABEL[r.status]}</Badge></td>
                  <td className="whitespace-nowrap px-3 py-1.5 text-muted-foreground">{r.handledByUserName ?? '—'}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    <div className="flex items-center gap-1.5">
                      {canExport && (
                        <Button
                          variant="outline" size="sm" className="h-7 px-2"
                          onClick={() => downloadDsarExport(r.id).catch((e) => toast.error(apiError(e)))}
                        >
                          <Download className="h-3.5 w-3.5" /> {t('settings:dsar.export')}
                        </Button>
                      )}
                      {canUpdate && r.status === 'pending' && (
                        <Button variant="outline" size="sm" className="h-7 px-2"
                          onClick={() => update.mutate({ id: r.id, status: 'in_progress' }, { onError: (e) => toast.error(apiError(e)) })}>
                          {t('settings:dsar.startProcessing')}
                        </Button>
                      )}
                      {canUpdate && r.type !== 'erasure' && r.status === 'in_progress' && (
                        <Button variant="outline" size="sm" className="h-7 px-2"
                          onClick={() => update.mutate({ id: r.id, status: 'done' }, { onError: (e) => toast.error(apiError(e)) })}>
                          {t('settings:dsar.complete')}
                        </Button>
                      )}
                      {canErase && r.type === 'erasure' && r.status !== 'done' && !r.contact.deleted && (
                        <Button variant="destructive" size="sm" className="h-7 px-2" onClick={() => setEraseTarget(r)}>
                          <ShieldAlert className="h-3.5 w-3.5" /> {t('settings:dsar.eraseAction')}
                        </Button>
                      )}
                      {canUpdate && r.status === 'pending' && (
                        <Button variant="ghost" size="sm" className="h-7 px-2 text-muted-foreground"
                          onClick={() => update.mutate({ id: r.id, status: 'rejected' }, { onError: (e) => toast.error(apiError(e)) })}>
                          {t('settings:dsar.reject')}
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {data && data.total > data.limit && (
        <div className="flex items-center justify-end gap-2 text-xs">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>{t('settings:dsar.prevPage')}</Button>
          <span className="text-muted-foreground">{t('settings:dsar.pageOf', { page, total: Math.ceil(data.total / data.limit) })}</span>
          <Button variant="outline" size="sm" disabled={page >= Math.ceil(data.total / data.limit)} onClick={() => setPage((p) => p + 1)}>{t('settings:dsar.nextPage')}</Button>
        </div>
      )}

      <CreateDsarDialog open={openCreate} onOpenChange={setOpenCreate} />
      <EraseDsarDialog request={eraseTarget} onOpenChange={(open) => !open && setEraseTarget(null)} />
    </div>
  )
}

// ── Tạo yêu cầu mới ──────────────────────────────────────────────────

function useDebounced<T>(value: T, ms: number): T {
  const [v, setV] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return v
}

function CreateDsarDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('settings')
  const [search, setSearch] = useState('')
  const [contactId, setContactId] = useState('')
  const [type, setType] = useState<DsarType>('access')
  const [note, setNote] = useState('')
  const debounced = useDebounced(search.trim(), 300)
  const contactsQ = useContacts({ search: debounced || undefined, limit: 8 })
  const create = useCreateDsarRequest()

  const reset = () => { setSearch(''); setContactId(''); setType('access'); setNote('') }

  return (
    <Dialog open={open} onOpenChange={(v) => { onOpenChange(v); if (!v) reset() }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{t('settings:dsar.createDialogTitle')}</DialogTitle>
          <DialogDescription>{t('settings:dsar.createDialogDescription')}</DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          <div>
            <label className="mb-1 block text-xs font-medium">{t('settings:dsar.contactLabel')}</label>
            <Input value={search} onChange={(e) => { setSearch(e.target.value); setContactId('') }} placeholder={t('settings:dsar.searchPlaceholder')} className="h-8" />
            {search && !contactId && (
              <div className="mt-1 max-h-40 overflow-y-auto rounded-md border">
                {contactsQ.isLoading ? (
                  <p className="p-2 text-xs text-muted-foreground">{t('settings:dsar.searching')}</p>
                ) : (contactsQ.data?.contacts.length ?? 0) === 0 ? (
                  <p className="p-2 text-xs text-muted-foreground">{t('settings:dsar.noContactFound')}</p>
                ) : (
                  contactsQ.data!.contacts.map((c) => (
                    <button
                      key={c.id} type="button"
                      className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent"
                      onClick={() => { setContactId(c.id); setSearch(c.crmName || c.fullName || c.phone || '') }}
                    >
                      <div className="font-medium">{c.crmName || c.fullName || t('settings:dsar.noName')}</div>
                      <div className="text-muted-foreground">{c.phone}</div>
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">{t('settings:dsar.typeLabel')}</label>
            <Select value={type} onValueChange={(v) => setType(v as DsarType)}>
              <SelectTrigger className="h-8"><SelectValue /></SelectTrigger>
              <SelectContent>
                {(Object.keys(DSAR_TYPE_LABEL) as DsarType[]).map((typeOption) => (
                  <SelectItem key={typeOption} value={typeOption}>{DSAR_TYPE_LABEL[typeOption]}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="mb-1 block text-xs font-medium">{t('settings:dsar.noteLabel')}</label>
            <textarea
              value={note} onChange={(e) => setNote(e.target.value)} rows={2}
              placeholder={t('settings:dsar.notePlaceholder')}
              className="w-full rounded-md border border-input bg-card px-3 py-1.5 text-xs focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/15"
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          <Button
            disabled={!contactId || create.isPending}
            onClick={() =>
              create.mutate({ contactId, type, note: note.trim() || undefined }, {
                onSuccess: () => { toast.success(t('settings:dsar.createdToast')); onOpenChange(false); reset() },
                onError: (e) => toast.error(apiError(e)),
              })
            }
          >
            {create.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('settings:dsar.createAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

// ── Xoá vĩnh viễn — xác nhận 2 lần ───────────────────────────────────

function EraseDsarDialog({ request, onOpenChange }: { request: DsarRequest | null; onOpenChange: (open: boolean) => void }) {
  const { t } = useTranslation('settings')
  const [acked, setAcked] = useState(false)
  const [typed, setTyped] = useState('')
  const detail = useDsarRequest(request?.id)
  const erase = useEraseDsarRequest()

  useEffect(() => {
    if (!request) { setAcked(false); setTyped('') }
  }, [request])

  if (!request) return null
  const phrase = detail.data?.erasureConfirmPhrase ?? null
  const matched = !!phrase && typed.trim() === phrase

  return (
    <Dialog open onOpenChange={(v) => !v && onOpenChange(false)}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-destructive">
            <AlertTriangle className="h-5 w-5" /> {t('settings:dsar.eraseDialogTitle')}
          </DialogTitle>
          <DialogDescription asChild>
            <div className="space-y-2 text-sm">
              <p className="rounded-md bg-destructive/10 px-3 py-2 font-medium text-destructive">
                {t('settings:dsar.eraseWarning')}
              </p>
              <p className="text-muted-foreground">
                {t('settings:dsar.eraseHint')}
              </p>
            </div>
          </DialogDescription>
        </DialogHeader>

        {detail.isLoading ? (
          <Loading />
        ) : !phrase ? (
          <p className="text-sm text-destructive">{t('settings:dsar.noContactInfo')}</p>
        ) : (
          <div className="space-y-3">
            <label className="flex items-start gap-2 text-sm">
              <input type="checkbox" checked={acked} onChange={(e) => setAcked(e.target.checked)} className="mt-0.5" />
              <span><Trans t={t} i18nKey="settings:dsar.ackLabel" components={{ b: <strong /> }} /></span>
            </label>
            {acked && (
              <div>
                <label className="mb-1 block text-xs font-medium">
                  {t('settings:dsar.confirmPhraseLabel')} <span className="font-mono text-destructive">{phrase}</span>
                </label>
                <Input value={typed} onChange={(e) => setTyped(e.target.value)} className="h-8" autoFocus />
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>{t('common:actions.cancel')}</Button>
          <Button
            variant="destructive"
            disabled={!acked || !matched || erase.isPending}
            onClick={() =>
              erase.mutate({ id: request.id, confirmText: typed.trim() }, {
                onSuccess: (r) => {
                  toast.success(t('settings:dsar.eraseSuccessToast', {
                    conversations: r.deletedConversations, messages: r.deletedMessages, files: r.deletedFiles,
                  }))
                  onOpenChange(false)
                },
                onError: (e) => toast.error(apiError(e)),
              })
            }
          >
            {erase.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
            {t('settings:dsar.eraseAction')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
